/**
 * @fileoverview `handleAuthorizationAction` is a handler for authorization actions using the legacy
 * Flow Authorization models as part of the payment
 */

import { isNil } from '@flowio/is';
import assertUnreachable from '../utilities/assertUnreachable';
import { resolveElement } from '../utilities/elementHelpers';

import handleOgAdyenAction from './adyen/handleOgAdyenAction';
import handleOgAuthInlineAction from './handleOgAuthInlineAction';
import handlePostRedirect from './handlePostRedirect';
import handleOgStripeAction from './stripe/handleOgStripeAction';

type AuthorizationActionConfig = {
  organization: string,
  locale?: string,
  mountElement: HTMLElement | string,
  sessionId: string,
};

export type Error = {
  name: string,
  reason: object | string,
};

type AuthorizationActionParameters = {
  authAction: io.flow.v0.unions.AuthorizationResultAction,
  onComplete: (result: io.flow.v0.models.AuthorizationResult) => void,
  onFailure: (error?: Error) => void,
  config: AuthorizationActionConfig,
};

export default function handleAuthorizationAction(params: AuthorizationActionParameters): void {
  const {
    authAction,
    onComplete,
    onFailure,
    config,
  } = params;

  const mount = resolveElement(config.mountElement);
  if (isNil(mount)) {
    onFailure({
      name: 'FlowError',
      reason: 'Could not find mount element',
    });
    return;
  }

  switch (authAction.discriminator) {
    case 'authorization_result_action_native': {
      if (isNil(authAction.details)) {
        onFailure({
          name: 'FlowError',
          reason: 'Missing action details',
        });
        break;
      }

      const authDetails = authAction.details;
      const authKey = authAction.authorization.key;
      const { sessionId } = config;

      switch (authDetails.discriminator) {
        case 'adyen_native_action_details':
          handleOgAdyenAction(
            authDetails,
            mount,
            sessionId,
            config.organization,
            authKey,
            onComplete,
            onFailure,
            config.locale,
          );
          break;
        case 'threeds_challenge_action_details':
        case 'threeds_identify_action_details':
          handleOgAuthInlineAction({
            authResultAction: authAction,
            authKey,
            organizationId: config.organization,
            sessionId,
            mount,
            onComplete,
            onFailure,
          });
          break;
        case 'stripe_authorization_result_action_details':
          handleOgStripeAction({
            authActionDetails: authDetails,
            authKey,
            organizationId: config.organization,
            sessionId,
          }).then((auth) => {
            onComplete(auth.result);
          }).catch((e) => {
            const errorMsg = e instanceof Error ? e.message : String(e);
            onFailure({
              name: 'FlowError',
              reason: errorMsg,
            });
          });
          break;
        case 'klarna_sdk_authorization_result_action_details':
          // not currently supported.
          break;
        case 'select_issuer_option_action_details':
          // not currently supported.
          break;
        case 'applepay_sdk_create_result_action_details':
          // not currently supported.
          break;
        case 'applepay_sdk_validate_result_action_details':
          // not currently supported.
          break;
        default:
          assertUnreachable(authDetails);
      }
      break;
    }
    case 'authorization_result_action_get':
      if (!isNil(authAction.url)) {
        window.location.assign(authAction.url);
      } else {
        onFailure({
          name: 'FlowError',
          reason: 'No redirect URL available',
        });
      }
      break;
    case 'authorization_result_action_post':
      handlePostRedirect(
        authAction.url,
        mount,
        authAction.parameters,
      );
      break;
    case 'authorization_result_action_wait':
    default: break;
  }
}
